import { lazy, Suspense } from "react";
import { Box, CircularProgress, Typography } from "@mui/material";
import { ErrorBoundary } from "react-error-boundary";

// Must stay type-only: a value import from the Mapbox module re-creates a
// static edge, folding Mapbox GL back into this chunk and undoing the split
// with nothing at runtime to show that it happened.
import type { LocationPrecisionOverrideMapProps } from "./LocationPrecisionOverrideMapbox.js";

const LocationPrecisionOverrideMapbox = lazy(async () => ({
  default: (await import("./LocationPrecisionOverrideMapbox.js"))
    .LocationPrecisionOverrideMapbox,
}));

const centred = {
  display: "flex",
  justifyContent: "center",
  padding: "2em",
} as const;

/**
 * Defers Mapbox GL, which is heavy enough to be worth keeping off the initial
 * load, so it is fetched only when someone asks to see the map.
 */
export const LocationPrecisionOverrideMap = (
  props: LocationPrecisionOverrideMapProps,
) => (
  <ErrorBoundary
    // Reload, not retry: the failure being recovered from is a deployment
    // having replaced the chunk this document asked for, and only a new
    // document names chunks that still exist.
    fallback={
      <Box sx={centred}>
        <Typography sx={{ color: "text.secondary" }}>
          Could not load the map. Reload the page to try again.
        </Typography>
      </Box>
    }
  >
    <Suspense
      fallback={
        <Box sx={centred}>
          <CircularProgress />
        </Box>
      }
    >
      <LocationPrecisionOverrideMapbox {...props} />
    </Suspense>
  </ErrorBoundary>
);
